import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import * as makeApi from '../../api/makeApi';
import { bust } from '../../utils/apiCache';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function MakesTab() {
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('MANAGE_MASTER_DATA');

  const toggleRow = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => selectedIds.size === makes.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(makes.map((m) => m.id)));
  const allSelected = makes.length > 0 && selectedIds.size === makes.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < makes.length;

  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      const res = await makeApi.batchRemove([...selectedIds]);
      showSnackbar(res.message || `${selectedIds.size} make(s) deleted.`, 'success');
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try { setMakes(await makeApi.getAll()); }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setName(''); setDialogOpen(true); };
  const openEdit = (row) => { setEditItem(row); setName(row.name); setDialogOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editItem) { await makeApi.update(editItem.id, { name }); showSnackbar('Make updated', 'success'); }
      else { await makeApi.create({ name }); showSnackbar('Make created', 'success'); }
      bust('stats'); // Dashboard's Makes count card reads from this same cache
      setDialogOpen(false); load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await makeApi.remove(deleteTarget.id);
      showSnackbar('Make deleted', 'success');
      bust('stats');
      setDeleteTarget(null); load();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Make</Button>
        )}
      </Box>

      {/* Bulk action toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} make(s) selected</Typography>
          <Button variant="contained" color="error" size="small" startIcon={<DeleteSweepIcon />} onClick={() => setBulkDeleteOpen(true)}>
            Delete Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>Clear</Button>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#F5F5F5' }}>
            {canDelete && (
              <TableCell padding="checkbox">
                <Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={toggleAll} />
              </TableCell>
            )}
            <TableCell>#</TableCell>
            <TableCell>Make Name</TableCell>
            <TableCell>Linked Prices</TableCell>
            {(canManage || canDelete) && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {makes.map((m, i) => (
            <TableRow key={m.id} hover selected={selectedIds.has(m.id)} sx={{ bgcolor: selectedIds.has(m.id) ? '#FFF8E1 !important' : undefined }}>
              {canDelete && (
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={selectedIds.has(m.id)} onChange={() => toggleRow(m.id)} />
                </TableCell>
              )}
              <TableCell>{i + 1}</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>{m.name}</TableCell>
              <TableCell><Chip label={m._count?.prices || 0} size="small" /></TableCell>
              {(canManage || canDelete) && (
                <TableCell align="right">
                  {canManage && (
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(m)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete (cascades all prices and history)">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(m)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {makes.length === 0 && (
            <TableRow><TableCell colSpan={canDelete ? 5 : (canManage ? 4 : 3)} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No makes found</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Make' : 'New Make'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Make Name" fullWidth value={name}
            onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Make"
        message={`Delete "${deleteTarget?.name}"? This will also remove all associated prices, price history, and any estimation line items referencing this make. This cannot be undone.`}
        confirmLabel="Yes, Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} Make(s)`}
        message={`Delete ${selectedIds.size} selected make(s) and ALL associated prices, price history, and estimation references?\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </Box>
  );
}
